import { View, Text, StyleSheet, TouchableOpacity } from "react-native";
import React, { useEffect, useRef, useState } from "react";

import Attribute from "../Firestore/Attribute";

import GlobalStyles from "../../Resources/Styles";
import Colors from "../../Resources/Color";
import Fonts from "../../Resources/Fonts";
import { Feather, MaterialIcons } from "@expo/vector-icons";

import { Octicons } from "@expo/vector-icons";
import CustomAlert from "../Components/CustomAlert";
import { observer, Observer } from "mobx-react";
import { reaction, runInAction } from "mobx";

import { useTranslation } from "react-i18next";
import { useScreenDimensions, useDevice } from "../_layout";
import AddPredefinedAttributeView from "./AddPredefinedAttributeView";
import IconEmptyView from "../Components/IconEmptyView";
import { useDrag, useDrop } from "react-dnd";

const PredefinedAttributeLayoutView = observer(({ coach, block, exercise }) => {
  const { t, i18n } = useTranslation();
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();

  // const coach = libraryCoach ? libraryCoach : client.coach;
  const [showingDeleteAttributes, setShowingDeleteAttributes] = useState(false);
  const [showingDeleteAttributesAlert, setShowingDeleteAttributesAlert] =
    useState(false);
  const [attributeToDelete, setAttributeToDelete] = useState("");
  const deleteAttributesAlertTitle =
    "deletePredefinedAttributeAlertTitle-string";
  const deleteAttributesAlertText =
    "¿Estás seguro/a de eliminar el atributo predefinido?";
  const [showingAddPredefinedAttribute, setShowingAddPredefinedAttribute] =
    useState(false);
  const [selectedAttTitle, setSelectedAttTitle] = useState("");

  const predefinedAttributes = coach.customPredefinedAttributes
    ? coach.customPredefinedAttributes
    : [];

  const [refresh, setRefresh] = useState(false);

  // Function to move attributes within the same type (title)
  // Subtitles are no longer rendered separately, so ordering is per-title.
  const movePredefinedAttribute = (dragIndex, hoverIndex, filteredForTitle) => {
    runInAction(() => {
      if (dragIndex === hoverIndex) return;

      // Get the dragged attribute and the target attribute
      const draggedAttribute = filteredForTitle[dragIndex];
      const targetAttribute = filteredForTitle[hoverIndex];

      if (!draggedAttribute || !targetAttribute) return;

      // Store original order values before making changes
      const originalOrders = new Map();
      filteredForTitle.forEach((attribute) => {
        originalOrders.set(attribute.id, attribute.order);
      });

      // Create a new array with the moved attribute
      const newFiltered = [...filteredForTitle];
      newFiltered.splice(dragIndex, 1);
      newFiltered.splice(hoverIndex, 0, draggedAttribute);

      // Update orders for the reordered items to be consecutive (0, 1, 2, 3...)
      // Ordering is per-title (subtitle is ignored for ordering).
      newFiltered.forEach((attribute, index) => {
        const originalIndex = coach.customPredefinedAttributes.findIndex(
          (a) => a.id === attribute.id
        );
        if (originalIndex !== -1) {
          const newOrder = index;
          const originalOrder = originalOrders.get(attribute.id);

          // Update the order
          coach.customPredefinedAttributes[originalIndex].order = newOrder;

          // Only add to transaction if the order actually changed
          if (originalOrder !== newOrder) {
            coach.updateCoachTransactionAttributes(attribute.id);
          }
        }
      });
    });
  };

  useEffect(() => {
    coach.resetAttributes();
    if (exercise) {
      exercise.attributes.forEach((att) => {
        const filtered = predefinedAttributes.filter(
          (att2) => att2.name === att.name
        );
        filtered.forEach((filteredAtt) => {
          coach.selectAttribute(exercise, filteredAtt, t, true);
        });
      });
    }
  }, [exercise, refresh]);

  useEffect(() => {
    if (exercise) {
      const dispose = reaction(
        () => ({
          updateSets: exercise.updateSets,
        }),
        () => {
          setRefresh((prev) => !prev);
        }
      );
      return () => dispose();
    }
  }, [exercise]);

  const PredefinedAttibuteTypeLayoutView = observer(({ title }) => {
    const filtered = coach
      .getFilteredAttributesArray(title)
      .sort((a, b) => a.order - b.order);

    const [isHovered, setIsHovered] = useState(false);

    const AttributeButton = observer(
      ({ attribute, index, filteredForTitle }) => {
        const dragType = `attribute-${title}`;

        const [isHovered, setIsHovered] = useState(false);
        const ref = useRef(null);

        // Drag and Drop setup - only enabled when showingDeleteAttributes is true
        const [{ isOver }, drop] = useDrop({
          accept: dragType,
          drop: (item, monitor) => {
            if (item.index !== index) {
              movePredefinedAttribute(item.index, index, filteredForTitle);
              coach.startOrResetBatchTimer();
            }
          },
          collect: (monitor) => ({
            isOver: monitor.isOver(),
          }),
        });

        const [{ isDragging }, drag] = useDrag({
          canDrag: showingDeleteAttributes,
          type: dragType,
          item: showingDeleteAttributes
            ? {
                type: dragType,
                id: attribute.id,
                index,
              }
            : null,
          end: () => {
            if (showingDeleteAttributes) {
              coach.startOrResetBatchTimer();
              console.log("Finished dragging:", attribute.id);
            }
          },
          collect: (monitor) => ({
            isDragging: monitor.isDragging(),
          }),
        });

        useEffect(() => {
          if (showingDeleteAttributes) {
            drag(drop(ref));
          } else {
            // Disconnect drag and drop when not in delete mode
            drag(null);
            drop(null);
          }
        }, [drag, drop, showingDeleteAttributes]);

        const select = () => {
          if (showingDeleteAttributes) {
            setAttributeToDelete(attribute.id);
            setShowingDeleteAttributesAlert(true);
          } else {
            if (exercise) {
              if (attribute.isSelected) {
                coach.deselectAttribute(exercise, attribute, t);
              } else {
                coach.selectAttribute(exercise, attribute, t, false);
                coach.setSearchTermAttr("");
              }
            } else {
              if (block) {
                block.addNewSlot(null, t, attribute);
              }
            }
          }
        };
        return (
          <TouchableOpacity
            ref={ref}
            onPress={select}
            style={{ maxWidth: "100%" }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <View
              style={[
                styles.AttributeButton,
                {
                  backgroundColor: isHovered
                    ? showingDeleteAttributes
                      ? Colors.redColor
                      : "rgba(63, 123, 141, 0.65)"
                    : attribute.isSelected
                    ? Colors.bubbleColor
                    : Colors.whiteColor,
                  borderColor: isHovered
                    ? showingDeleteAttributes
                      ? Colors.redColor
                      : "rgba(63, 123, 141, 0.65)"
                    : attribute.isSelected
                    ? Colors.bubbleColor
                    : Colors.grayColor,
                  opacity: isDragging ? 0 : isOver ? 0.4 : 1,
                },
              ]}
            >
              <View style={[GlobalStyles.HStack, { alignItems: "center" }]}>
                {showingDeleteAttributes && (
                  <MaterialIcons
                    name="drag-indicator"
                    size={12}
                    color={isHovered ? Colors.whiteColor : Colors.grayColor}
                    style={{
                      marginLeft: 4,
                      marginRight: -2,
                    }}
                  />
                )}
                <Text
                  key={index}
                  style={{
                    fontSize: 12,
                    paddingVertical: 5,
                    paddingLeft: 5,
                    paddingRight: showingDeleteAttributes ? 0 : 5,
                    color:
                      attribute.isSelected || isHovered
                        ? Colors.whiteColor
                        : Colors.grayColor,
                    width: "100%",
                  }}
                >
                  {t(attribute.name)}
                </Text>
                {showingDeleteAttributes && (
                  <Feather
                    name="x"
                    size={12}
                    color={isHovered ? Colors.whiteColor : Colors.redColor}
                    style={{ padding: 5 }}
                  />
                )}
              </View>
            </View>
          </TouchableOpacity>
        );
      }
    );

    const AddAttributeButton = ({ title }) => {
      const [isHovered, setIsHovered] = useState(false);
      const action = () => {
        // if (!coach.isOffline) {
        setSelectedAttTitle(title);
        setShowingAddPredefinedAttribute(true);
        // }
      };
      return (
        <TouchableOpacity
          onPress={action}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <View
            style={[
              styles.AttributeButton,
              {
                backgroundColor: isHovered
                  ? "rgba(63, 123, 141, 0.65)"
                  : Colors.whiteColor,
                borderColor: isHovered
                  ? "rgba(63, 123, 141, 0.65)"
                  : Colors.grayColor,
                paddingHorizontal: 2,
                opacity: 1,
              },
            ]}
          >
            <Text
              style={{
                fontSize: 12,
                padding: 5,
                color: isHovered ? Colors.whiteColor : Colors.grayColor,
              }}
            >
              +
            </Text>
          </View>
        </TouchableOpacity>
      );
    };

    return (
      (coach.searchTermAttr === "" || (filtered && filtered.length > 0)) && (
        <View style={{}}>
          <View
            style={[GlobalStyles.HStack, { justifyContent: "space-between" }]}
          >
            <Text style={Fonts.bold14}>{t(title)}</Text>
            {title === Attribute.AttributeTitles.equipment && (
              <TouchableOpacity
                onPress={() => {
                  // if (!coach.isOffline) {
                  setShowingDeleteAttributes(!showingDeleteAttributes);
                  // }
                }}
                onMouseEnter={() => !isMobile && setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <Feather
                  name="edit-3"
                  size={18}
                  color={
                    showingDeleteAttributes
                      ? Colors.bubbleColor
                      : Colors.grayColor
                  }
                  style={{
                    paddingLeft: 5,
                    paddingBottom: 0,
                    opacity: isHovered ? 0.5 : 1,
                  }}
                />
              </TouchableOpacity>
            )}
          </View>
          <View style={{ ...styles.AttributeFlex, gap: 6 }}>
            {filtered.map((attribute, index) => (
              <AttributeButton
                key={attribute.id}
                attribute={attribute}
                index={index}
                filteredForTitle={filtered}
              />
            ))}
            {!showingDeleteAttributes && <AddAttributeButton title={title} />}
          </View>
        </View>
      )
    );
  });

  if (!coach.hasMatchingAttributes()) {
    return (
      <IconEmptyView
        iconLibrary={"MaterialCommunityIcons"}
        mainText={"emptyAttributesTitle-string"}
        subText={"noAttributesFoundText-string"}
        iconName={"magnify-plus-outline"}
        marginTop={32}
        marginBottom={16}
      />
    );
  }
  return (
    <View style={{ gap: 16 }}>
      <PredefinedAttibuteTypeLayoutView
        title={Attribute.AttributeTitles.equipment}
      />
      <PredefinedAttibuteTypeLayoutView
        title={Attribute.AttributeTitles.tempo}
      />
      <PredefinedAttibuteTypeLayoutView title={Attribute.AttributeTitles.rom} />
      <PredefinedAttibuteTypeLayoutView
        title={Attribute.AttributeTitles.stanceGrip}
      />
      <PredefinedAttibuteTypeLayoutView
        title={Attribute.AttributeTitles.loadAccomodation}
      />
      <PredefinedAttibuteTypeLayoutView
        title={Attribute.AttributeTitles.accessories}
      />
      <PredefinedAttibuteTypeLayoutView
        title={Attribute.AttributeTitles.other}
      />
      {showingAddPredefinedAttribute && selectedAttTitle !== "" && (
        <AddPredefinedAttributeView
          coach={coach}
          isVisible={showingAddPredefinedAttribute}
          onClose={() => setShowingAddPredefinedAttribute(false)}
          title={selectedAttTitle}
          subtitle={""}
          setTitle={() => setSelectedAttTitle("")}
          setSubtitle={() => {}}
        />
      )}
      {showingDeleteAttributesAlert && (
        <CustomAlert
          isVisible={showingDeleteAttributesAlert}
          onClose={() => setShowingDeleteAttributesAlert(false)}
          title={deleteAttributesAlertTitle}
          text={deleteAttributesAlertText}
          action={() => setShowingDeleteAttributesAlert(false)}
          specialCTA={"delete-string"}
          coach={coach}
          attributeToDelete={attributeToDelete}
        />
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#EFF4F5",
    paddingTop: 30,
    marginBottom: 20,
  },
  subtitle: {
    fontSize: 18,
    fontWeight: "500",
    color: "gray",
  },
  tabText: {
    fontSize: 13,
    fontWeight: "500",
  },
  underline: {
    height: 2,
    width: "100%",
    alignSelf: "center",
    marginTop: 2,
  },
  separator: {
    width: 0.1,
    height: "100%",
    opacity: 0.2,
  },
  AttributeFlex: {
    flex: 1,
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 8,
  },
  AttributeButton: {
    borderRadius: 8,
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
  },
});

export default PredefinedAttributeLayoutView;
